import type { DayWindow } from '../daily-brief/dayWindows';
import type { CompareMode, DatePreset } from '../types';

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function labelRange(from: Date, to: Date): string {
  const a = from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const b = to.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return a === b ? a : `${a} – ${b}`;
}

function makeWindow(start: Date, end: Date): DayWindow {
  return {
    start: startOfLocalDay(start),
    end: endOfLocalDay(end),
    label: labelRange(start, end),
    isoFrom: isoDate(startOfLocalDay(start)),
    isoTo: isoDate(startOfLocalDay(end)),
  };
}

function daysInclusive(start: Date, end: Date): number {
  const s = startOfLocalDay(start).getTime();
  const e = startOfLocalDay(end).getTime();
  return Math.round((e - s) / 86_400_000) + 1;
}

/** Current dashboard period window from toolbar preset (local calendar). */
export function periodWindow(preset: DatePreset, now = new Date()): DayWindow {
  const end = startOfLocalDay(now);
  if (preset === 'today') return makeWindow(end, end);
  const start = new Date(end);
  if (preset === '7d') start.setDate(start.getDate() - 6);
  else if (preset === '90d') start.setDate(start.getDate() - 89);
  else if (preset === 'custom') start.setDate(start.getDate() - 29);
  else start.setDate(start.getDate() - 29); // 30d default
  return makeWindow(start, end);
}

export function compareWindow(
  current: DayWindow,
  mode: CompareMode,
): DayWindow | null {
  if (mode === 'none') return null;
  const len = daysInclusive(current.start, current.end);
  if (mode === 'prior_year') {
    const start = new Date(current.start);
    const end = new Date(current.end);
    start.setFullYear(start.getFullYear() - 1);
    end.setFullYear(end.getFullYear() - 1);
    return makeWindow(start, end);
  }
  // prior_period: equal length immediately before current
  const end = new Date(current.start);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - (len - 1));
  return makeWindow(start, end);
}

/** Last N local day windows ending at `end` (for sparklines). */
export function trailingDayWindows(end: Date, n: number): DayWindow[] {
  const days: DayWindow[] = [];
  const e = startOfLocalDay(end);
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(e);
    d.setDate(d.getDate() - i);
    days.push(makeWindow(d, d));
  }
  return days;
}

/** Every local calendar day in `[start, end]` inclusive. */
export function daysInWindow(win: DayWindow): DayWindow[] {
  const days: DayWindow[] = [];
  const end = startOfLocalDay(win.end).getTime();
  let cur = startOfLocalDay(win.start);
  while (cur.getTime() <= end) {
    days.push(makeWindow(cur, cur));
    cur = new Date(cur);
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}
